import React, { FC } from 'react';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { closeModal } from '@src/redux/actions/modal';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { useAppDispatch } from '@src/redux';
import { OpeningStateEnum, useRepublishOpeningDataQuery, useRepublishOpeningMutation } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import {
  DetailsList,
  FooterInfoBlock,
  ModalInfo,
  ModalActions,
  ModalContainer,
  ModalTitle,
  BenefitsList,
} from '../components';
import { RepublishOpeningModalProps } from './props';

export const RepublishOpeningModal: FC<RepublishOpeningModalProps> = ({ openingId, onUpdateOpening }) => {
  const dispatch = useAppDispatch();

  const {
    loading: isQueryLoading,
    error,
    data,
  } = useRepublishOpeningDataQuery({
    fetchPolicy: 'network-only',
    onError: () => setErrorFlashMessage(),
  });

  const [republishOpening, { loading: isMutationLoading }] = useRepublishOpeningMutation({
    onCompleted: () => {
      setSuccessFlashMessage('Total slots limit has been successfully increased!');
    },
  });

  if (isQueryLoading) {
    return <ModalContainer isLoading />;
  }

  if (error) {
    closeModal();

    return null;
  }

  const {
    customer: {
      id: customerId,
      planInfo: {
        currency: { symbol },
        openingRepublishingPrice,
      },
    },
  } = data;

  return (
    <ModalContainer>
      <ModalTitle>Confirm republishing</ModalTitle>
      <ModalInfo>You will be charged a one-time fee for republishing the job offer.</ModalInfo>
      <BenefitsList benefits={['Resurface your job to talent', 'Reach a larger audience']} />
      <DetailsList
        details={[
          {
            name: 'Price',
            value: (
              <strong>
                {symbol}
                {openingRepublishingPrice}
              </strong>
            ),
          },
        ]}
      />
      <ModalActions
        actions={[
          {
            isDisabled: isMutationLoading,
            name: 'Confirm',
            onClick: () => {
              trackEvent({
                context: MetricContext.BILLING,
                interaction: MetricAction.REPUBLISH_OPENING,
                payload: {
                  'Customer': customerId,
                  'Opening Republishing Price': openingRepublishingPrice,
                },
              });

              republishOpening({
                variables: {
                  openingId: openingId,
                },
              }).finally(() => {
                onUpdateOpening({
                  id: openingId,
                  state: OpeningStateEnum.ACTIVE,
                });
                dispatch(closeModal());
              });
            },
          },
          {
            buttonAppearance: 'secondary',
            isDisabled: isMutationLoading,
            name: 'Close',
            onClick: () => dispatch(closeModal()),
          },
        ]}
      />
      <FooterInfoBlock />
    </ModalContainer>
  );
};
